import { supabase } from '@/config/supabase';
import { useAuthStore } from '@/store/authStore';
import { getViewingOrgId } from '@/utils/effectiveOrgId';
import type { AuditAction, AuditLogEntry } from '@/types';

export interface LogAuditInput {
  action: AuditAction | string;
  resource_type: string;
  resource_id: string;
  description?: string;
  changes?: { before?: unknown; after?: unknown };
}

/**
 * Shared write helper for tables that have no DB audit trigger — the
 * `log_audit_event()` trigger (0001_initial_schema.sql §13.2) only covers
 * `policies`, `doa_matrices`, `doa_rules`, and `org_members`. Anything else
 * that needs an audit trail entry (Decision Check submit/withdraw/edit,
 * approve/reject/return — FR-040) must call this explicitly. Used by
 * decisionChecks.ts (T073) and decisionApprovals.ts (T093).
 *
 * Goes through the `create_audit_log_entry` RPC rather than a plain
 * `.insert()` — a direct client-side INSERT satisfying `audit_logs`'
 * WITH CHECK (organisation_id = get_current_org_id()) is rejected by the
 * live database for the same unexplained reason as decision_checks and
 * compliance_reviews (migrations 0031/0032) — same proven workaround.
 * This one matters more than most: because this function's own errors
 * are deliberately swallowed (see below), this was very likely silently
 * failing across a wide range of actions with zero visible symptom
 * before this fix (DECISION_LOG.md Decision 046).
 */
export async function logAudit(input: LogAuditInput): Promise<void> {
  const member = useAuthStore.getState().member;
  if (!member) return;

  const { error } = await supabase.rpc('create_audit_log_entry', {
    payload: {
      organisation_id: member.organisation_id,
      user_id: member.user_id,
      user_role: member.role,
      member_id: member.id,
      action: input.action,
      resource_type: input.resource_type,
      resource_id: input.resource_id,
      description: input.description,
      changes: input.changes ?? null,
    },
  });

  // An audit-log write failure must never block the underlying business
  // action from having already succeeded — log and move on rather than
  // throwing back into the caller's happy path.
  if (error) console.error('Failed to write audit log entry:', error);
}

export interface AuditLogFilters {
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  action?: string;
  resourceType?: string;
}

/** Audit Log Viewer — filterable by date range, user, action type, resource (FR-108). RLS restricts this to Manager role and above. */
export async function listAuditLog(filters: AuditLogFilters = {}, limit = 200): Promise<AuditLogEntry[]> {
  let query = supabase.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(limit);

  if (filters.dateFrom) query = query.gte('created_at', filters.dateFrom);
  if (filters.dateTo) query = query.lte('created_at', filters.dateTo);
  if (filters.userId) query = query.eq('user_id', filters.userId);
  if (filters.action) query = query.eq('action', filters.action);
  if (filters.resourceType) query = query.eq('resource_type', filters.resourceType);
  const viewingOrgId = getViewingOrgId();
  if (viewingOrgId) query = query.eq('organisation_id', viewingOrgId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return (data ?? []) as AuditLogEntry[];
}

/** Distinct action values already present in this org's audit log, for the Audit Log page's Action filter. */
export async function listAuditActionTypes(): Promise<string[]> {
  let query = supabase.from('audit_logs').select('action');
  const viewingOrgId = getViewingOrgId();
  if (viewingOrgId) query = query.eq('organisation_id', viewingOrgId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return Array.from(new Set((data ?? []).map((row) => row.action as string))).sort();
}

/** Distinct resource_type values already present in this org's audit log, for the Audit Log page's Resource Type filter. */
export async function listAuditResourceTypes(): Promise<string[]> {
  let query = supabase.from('audit_logs').select('resource_type');
  const viewingOrgId = getViewingOrgId();
  if (viewingOrgId) query = query.eq('organisation_id', viewingOrgId);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return Array.from(new Set((data ?? []).map((row) => row.resource_type as string))).sort();
}

/** Exports the (already-filtered) audit log as CSV text (FR-108). */
export function auditLogToCsv(entries: AuditLogEntry[]): string {
  const header = ['Timestamp', 'User', 'Role', 'Action', 'Resource Type', 'Resource', 'Description'];
  const rows = entries.map((e) => [
    e.created_at,
    e.user_email ?? e.user_id ?? '',
    e.user_role ?? '',
    e.action,
    e.resource_type,
    e.resource_name ?? e.resource_id ?? '',
    (e.description ?? '').replace(/"/g, '""'),
  ]);
  return [header, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
}
